import React from 'react';
import { ColumnConfig } from '../../config';
import StatusPill from '../../../../components/StatusPill';
import WorkflowStatusPill from '../../../../components/WorkflowStatusPill';

interface ProjectColumnProps {
  column: string;
  config: ColumnConfig;
  row: any;
}

/** "Nicos Terpkos" -> "NT"; a single word gives its first two letters. */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() ?? '';
};

/**
 * One register cell. Status is the same WorkflowStatusPill the project
 * modal's Stages tab uses, so a status reads the same in the list and the
 * modal; type is a tinted chip; the lead is an initials bubble.
 */
const ProjectColumn: React.FC<ProjectColumnProps> = ({ column, config, row }) => {
  const renderDisplayValue = () => {
    if (column === 'job_number') {
      return row.original.job_number ? row.original.job_number.toString() : 'Auto';
    }

    if (column === 'status' && config.type === 'badge') {
      const status: string | null = row.original.status || null;
      if (!status) return <StatusPill label="No status" tone="neutral" />;
      return <WorkflowStatusPill status={status} />;
    }

    if (column === 'types' && config.type === 'badge') {
      const type: string = row.original.types || 'Unknown';
      return (
        <span className={`ui-chip type-chip type-chip--${type.toLowerCase().replace(/\s+/g, '-')}`}>
          {type}
        </span>
      );
    }

    if (column === 'project_lead') {
      const lead = row.original.project_lead;
      if (!lead) return <span className="project-column-display__none">—</span>;
      const name = `${lead.first_name} ${lead.last_name}`;
      return (
        <span className="ui-avatar" title={name} aria-label={name}>
          {initials(name)}
        </span>
      );
    }

    if (column === 'client') {
      const client = row.original.client;
      return client ? <span title={client.company_name}>{client.company_name}</span> : '—';
    }

    if (column === 'description') {
      return <span title={row.original.description || undefined}>{row.original.description || ''}</span>;
    }

    if (column === 'created_at') {
      return new Date(row.original.created_at).toLocaleDateString();
    }

    return row.original[column] || '';
  };

  return <div className="project-column-display">{renderDisplayValue()}</div>;
};

export default ProjectColumn;
